import { useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import { ProjectQuotesPageDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { QuotesListTable } from 'modules/finance/components/Quotes/QuotesListTable'
import EmptyState from 'modules/project_management/components/EmptyState'
import { UpsellProjectQuotes } from 'modules/project_management/components/upsells/UpsellProjectQuotes'

import { quotesVisibility } from 'modules/project_management/lib/utils'
import NotFound from 'modules/core/components/NotFound'

const ProjectQuotesPage = () => {
  const { company, access, canRequestUpsell } = useCurrentSession()
  const { id } = useParams()
  const projectId = id ?? ''

  const { data, loading } = useQuery(ProjectQuotesPageDocument, {
    variables: {
      projectId,
      companyId: company.id,
      featureIds: [Features.sales_quotes_management, Features.projects_costs],
    },
    skip: !id,
  })

  if (!id) return null

  const hasQuickQuoteBuilderFF = !!data?.features.featuresConnection.nodes.find(
    (feature) => feature.name === 'dev_quick_quote_builder'
  )?.enabled
  if (!loading && !hasQuickQuoteBuilderFF) return <NotFound />

  const hasQuotesFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.sales_quotes_management
  )?.available

  const hasProjectsCostsFeature = !!data?.companies.featureAvailabilitiesConnection.nodes.find(
    (feature) => feature.id === Features.projects_costs
  )?.available

  const isOwnerOrAdmin = data?.projectManagement.project?._policies?.assignOwner.can ?? false
  const canManageProjectQuotes =
    data?.projectManagement.project?._policies?.manageProjectSalesDocuments.can ?? false

  const { canSeeSalesQuotesPageContent, canSeeSalesQuotesUpsell } = quotesVisibility(
    hasProjectsCostsFeature,
    canRequestUpsell,
    isOwnerOrAdmin,
    hasQuotesFeature,
    hasQuickQuoteBuilderFF
  )

  return (
    <>
      {canSeeSalesQuotesUpsell && <UpsellProjectQuotes />}

      {canSeeSalesQuotesPageContent &&
        (canManageProjectQuotes ? (
          <QuotesListTable
            companyId={company.id}
            locale={access.locale}
            canCreateQuote={false}
            projectId={projectId}
          />
        ) : (
          <EmptyState
            title={i18n.t('project_management.quotes.empty_state_no_permissions.title')}
            description={i18n.t('project_management.quotes.empty_state_no_permissions.description')}
            emoji={'✏️'}
          />
        ))}
    </>
  )
}

export default ProjectQuotesPage
